/**
 * Form state for adding one team member.
 *
 * Everything that can be derived is derived: visit types come from the clinical
 * titles, the Roger App username from the name and agency code, and whether an
 * app account is offered at all from the archetype and employment type. The
 * operator confirms those rather than retyping them, because the mistakes this
 * dialog exists to prevent were all of the "typed the wrong thing in a field they
 * didn't understand" kind.
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  DisciplineType,
  OnboardRogerDocMode,
  UserArchetype,
} from '../../../services/api';
import type { UserType } from '../../../services/api/models/UserType';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import type { AgencyClinicResponse } from '../types';
import { disciplineProse } from '../../../constants/disciplines';
import {
  disciplinesForTitles,
  titlesWithoutDiscipline,
} from '../../../utils/clinicalTitles';
import {
  buildRogerAppUsername,
  rogerAppUsernameProblem,
} from '../../../utils/rogerAppUsername';
import { NON_EMR_AGENCY_ROLE_SLUGS } from '../../../utils/rbacRoles';
import { ARCHETYPE_CLINIC_ROLE_SLUGS, archetypeOption } from './archetypes';

/**
 * Employment types that do not get a Roger App account by default. Only the
 * explicitly contracted ones -- PRN, per-diem and part-time staff are the
 * agency's own clinicians and do document visits, so treating "not staff" as
 * "no account" would leave them unable to chart.
 *
 * This is the only copy of the rule. The server used to hold a matching set, but
 * nothing there read it -- the operator can always override this default, so the
 * mode the dialog sends is the decision, and a second opinion on the server had
 * no way to be right.
 */
const EMPLOYMENT_WITHOUT_APP_ACCOUNT = new Set<string>([
  'CONTRACT',
  'CONTRACTOR_PRN',
  'CONTRACTOR_PART_TIME',
]);

export interface OnboardFormState {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  archetype: UserArchetype;
  clinicIds: string[];
  /** Set once the operator edits the clinic list, which stops the seeding. */
  clinicsTouched: boolean;
  userTypes: UserType[];
  employeeType: EmployeeType | '';
  employeeTypeOther: string;
  /** Set only when the operator edits the derived visit types. */
  disciplinesOverride: DisciplineType[] | null;
  appAccountMode: OnboardRogerDocMode;
  /** Pre-filled with the derived username; editable. */
  username: string;
  usernameEdited: boolean;
  /** The existing account to attach, when linking. */
  appAccountId: string;
  /**
   * Advanced, set only once the operator edits it. Null means "whatever the
   * archetype implies", which is what the server derives for itself -- so an
   * untouched dialog behaves exactly as it did before Advanced existed.
   */
  agencyRoleIdsOverride: string[] | null;
  clinicRoleIdsOverride: Record<string, string[]> | null;
}

const EMPTY: OnboardFormState = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  archetype: 'FIELD_CLINICIAN',
  clinicIds: [],
  clinicsTouched: false,
  userTypes: [],
  employeeType: '',
  employeeTypeOther: '',
  disciplinesOverride: null,
  appAccountMode: 'create',
  username: '',
  usernameEdited: false,
  appAccountId: '',
  agencyRoleIdsOverride: null,
  clinicRoleIdsOverride: null,
};

export type OnboardBlockerField =
  | 'email'
  | 'clinics'
  | 'titles'
  | 'appAccount'
  | 'agencyRoles';

export interface OnboardBlocker {
  field: OnboardBlockerField;
  message: string;
}

/** A Roger App account already in this agency, as the member list loaded it. */
export interface ExistingAppAccount {
  id: string;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  linkedToEmail?: string | null;
}

export interface UseOnboardUserOptions {
  clinics: AgencyClinicResponse[];
  agencyAbbreviation: string | null | undefined;
  /** Every Roger App account in the agency, for spotting one that already exists. */
  existingAppAccounts?: readonly ExistingAppAccount[];
  /** Every role in the agency, so Advanced can show what the archetype grants. */
  roles?: readonly { id: string; slug: string; scope?: string | null }[];
  /**
   * What each archetype's discretionary agency roles resolve to, from
   * `/agency-config`. Configurable per agency, so it cannot be derived here.
   */
  effectiveArchetypeRoles?: Record<string, string[]> | null;
  /**
   * Whether the dialog is showing its EMR surface. False for an agency that
   * does not run the Roger EMR, where the archetypes describe visits and
   * scheduling that agency does not have.
   */
  showEmrUserManagement?: boolean;
  /**
   * Whether the agency itself runs the Roger EMR.
   *
   * Deliberately separate from `showEmrUserManagement`, which a super user
   * expands. The Roger App offer follows this one, because whether somebody
   * needs an app account is a fact about the agency rather than about who is
   * filling the form in.
   */
  isAgencyRogerEmrEnabled?: boolean;
}

export function useOnboardUser({
  clinics,
  agencyAbbreviation,
  existingAppAccounts = [],
  roles = [],
  effectiveArchetypeRoles = null,
  showEmrUserManagement = true,
  isAgencyRogerEmrEnabled = true,
}: UseOnboardUserOptions) {
  // Back office is what is left once visits are off the table: no clinics, no
  // titles, and access that comes entirely from the roles the operator picks.
  // With the archetype question hidden it is never chosen, so it is the state
  // the form starts and resets to.
  const initial = useMemo(
    () =>
      showEmrUserManagement
        ? EMPTY
        : { ...EMPTY, archetype: 'BACK_OFFICE' as UserArchetype },
    [showEmrUserManagement]
  );
  const [form, setForm] = useState<OnboardFormState>(initial);

  const patch = useCallback((changes: Partial<OnboardFormState>) => {
    setForm(previous => ({ ...previous, ...changes }));
  }, []);

  const reset = useCallback(() => setForm(initial), [initial]);

  const option = archetypeOption(form.archetype);

  // Every clinic starts checked. Derived rather than seeded into state, so that
  // reopening the dialog or leaving and returning to a clinic-using archetype
  // starts checked again -- both reset `clinicIds` without the clinic list
  // itself changing, which an effect keyed on that list would sleep through.
  // The selector, the clinic-role matrix and the submission all read this.
  const effectiveClinicIds = useMemo(
    () =>
      form.clinicsTouched ? form.clinicIds : clinics.map(clinic => clinic.id),
    [clinics, form.clinicsTouched, form.clinicIds]
  );

  // Visit types follow the titles unless the operator has taken them over.
  const derivedDisciplines = useMemo(
    () => disciplinesForTitles(form.userTypes),
    [form.userTypes]
  );
  const disciplines = form.disciplinesOverride ?? derivedDisciplines;

  const uselessTitles = useMemo(
    () => titlesWithoutDiscipline(form.userTypes),
    [form.userTypes]
  );

  const derivedUsername = useMemo(
    () =>
      buildRogerAppUsername({
        firstName: form.firstName,
        lastName: form.lastName,
        agencyAbbreviation,
      }),
    [form.firstName, form.lastName, agencyAbbreviation]
  );

  // The field shows the derived value until the operator types over it, so the
  // preview keeps up with the name without discarding an edit.
  const username = form.usernameEdited
    ? form.username
    : (derivedUsername ?? '');

  /**
   * An account that looks like this person, found before they submit.
   *
   * Worth surfacing even when it belongs to somebody else: hiding it is what
   * produces "why won't it let me", and the same match is also the reason the
   * derived username is taken -- two people named J. Smith at one agency derive
   * the same one. Read-only either way; re-pointing an account would break the
   * other person's app login, so "same person" has to mean unlinking them first.
   */
  const existingAccountMatch = useMemo(() => {
    const first = form.firstName.trim().toLowerCase();
    const last = form.lastName.trim().toLowerCase();
    if (!first || !last) return null;

    const byName = existingAppAccounts.find(
      account =>
        (account.first_name ?? '').trim().toLowerCase() === first &&
        (account.last_name ?? '').trim().toLowerCase() === last
    );
    const byUsername = derivedUsername
      ? existingAppAccounts.find(
          account => account.username === derivedUsername
        )
      : undefined;

    const match = byName ?? byUsername;
    if (!match) return null;

    return {
      account: match,
      /** Whose account it is, when it already belongs to somebody. */
      linkedToEmail: match.linkedToEmail ?? null,
      /** Same name, so probably the same person. */
      sameName: match === byName,
    };
  }, [form.firstName, form.lastName, derivedUsername, existingAppAccounts]);

  const linkableAccounts = useMemo(
    () => existingAppAccounts.filter(account => !account.linkedToEmail),
    [existingAppAccounts]
  );

  // The archetype decides this while there is an archetype question to answer.
  // Without one everybody lands on back office, which offers no account -- and
  // an agency with no EMR is precisely a Roger App customer, so the offer has
  // to follow the agency rather than the archetype there.
  //
  // Keyed on the agency's own flag, not on whether the EMR surface is shown. A
  // super user sees the archetype cards at one of these agencies, and picking
  // back office there would otherwise submit no app account for somebody whose
  // agency runs on nothing else.
  const appAccountOffered = option.offersAppAccount || !isAgencyRogerEmrEnabled;
  const appAccountDefault =
    appAccountOffered && !EMPLOYMENT_WITHOUT_APP_ACCOUNT.has(form.employeeType);

  const setArchetype = useCallback(
    (archetype: UserArchetype) => {
      const next = archetypeOption(archetype);
      const nextOffersAppAccount =
        next.offersAppAccount || !isAgencyRogerEmrEnabled;
      setForm(previous => ({
        ...previous,
        archetype,
        // Clear what this archetype does not use, so a switch cannot leave
        // stale clinics or titles to be submitted invisibly.
        clinicIds: next.needsClinics ? previous.clinicIds : [],
        // Dropping the clinics drops the decision about them too, so coming back
        // to a clinic-using archetype offers the full list again rather than none.
        clinicsTouched: next.needsClinics ? previous.clinicsTouched : false,
        userTypes: next.needsTitles ? previous.userTypes : [],
        disciplinesOverride: next.needsTitles
          ? previous.disciplinesOverride
          : null,
        // Recomputed, not carried. Switching to an archetype with no app account
        // forces 'none', and carrying that back left a clinician silently without
        // one. A deliberate 'link' is a decision about a specific account, so it
        // survives; anything else follows the employment default, exactly as
        // setEmployeeType does.
        appAccountMode:
          previous.appAccountMode === 'link' && nextOffersAppAccount
            ? 'link'
            : nextOffersAppAccount &&
                !EMPLOYMENT_WITHOUT_APP_ACCOUNT.has(previous.employeeType)
              ? 'create'
              : 'none',
        // Both matrices are derived from the archetype, so an edit to either is an
        // edit about the archetype being left. Carrying a clinic-role edit across
        // the switch is the dangerous one: pick field clinician, edit Advanced,
        // switch to back office, and the clinician role survives at the clinics
        // chosen earlier -- while the server-side guard that would catch it does
        // not apply to an archetype with no clinic roles of its own. The result is
        // a back-office member who turns up in the scheduling picker.
        agencyRoleIdsOverride: null,
        clinicRoleIdsOverride: null,
      }));
    },
    [isAgencyRogerEmrEnabled]
  );

  const setEmployeeType = useCallback(
    (employeeType: EmployeeType) => {
      setForm(previous => {
        const offers =
          archetypeOption(previous.archetype).offersAppAccount ||
          !isAgencyRogerEmrEnabled;
        const wantsAccount =
          offers && !EMPLOYMENT_WITHOUT_APP_ACCOUNT.has(employeeType);
        return {
          ...previous,
          employeeType,
          // Employment type drives the default, so follow it -- unless the
          // operator has already chosen to link an existing account, which is a
          // decision about a specific account rather than a default.
          appAccountMode:
            previous.appAccountMode === 'link'
              ? 'link'
              : wantsAccount
                ? 'create'
                : 'none',
        };
      });
    },
    [isAgencyRogerEmrEnabled]
  );

  const linkExistingAccount = useCallback((accountId: string) => {
    setForm(previous => ({
      ...previous,
      appAccountMode: 'link',
      appAccountId: accountId,
    }));
  }, []);

  const roleIdBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    for (const role of roles) map[role.slug] = role.id;
    return map;
  }, [roles]);

  /**
   * What the archetype grants per clinic, before any editing.
   *
   * Shown rather than left blank because a matrix that shows nothing while the
   * server grants something is worse than no matrix -- it reads as "they get
   * none of these".
   */
  const derivedClinicRoleIds = useMemo(() => {
    const slugs = ARCHETYPE_CLINIC_ROLE_SLUGS[form.archetype];
    const map: Record<string, string[]> = {};
    for (const slug of slugs) {
      const roleId = roleIdBySlug[slug];
      if (roleId) map[roleId] = [...effectiveClinicIds];
    }
    return map;
  }, [form.archetype, effectiveClinicIds, roleIdBySlug]);

  /** The archetype's discretionary agency roles, as this agency resolves them. */
  const derivedAgencyRoleIds = useMemo(() => {
    const slugs = effectiveArchetypeRoles?.[form.archetype] ?? [];
    // Narrowed to what Advanced is actually showing when the EMR surface is
    // hidden. Preserving a role somebody already holds is one thing; seeding a
    // new member with an EMR role the operator cannot see, and then submitting
    // it the moment they touch anything, is another.
    const visible = showEmrUserManagement
      ? slugs
      : slugs.filter(slug => NON_EMR_AGENCY_ROLE_SLUGS.includes(slug));
    return visible
      .map(slug => roleIdBySlug[slug])
      .filter((id): id is string => !!id);
  }, [
    effectiveArchetypeRoles,
    form.archetype,
    roleIdBySlug,
    showEmrUserManagement,
  ]);

  // Derived until the operator edits, then theirs -- the same rule the visit
  // types follow, so adding a clinic still widens the matrix until it doesn't.
  const agencyRoleIds = form.agencyRoleIdsOverride ?? derivedAgencyRoleIds;
  const clinicRoleIds = form.clinicRoleIdsOverride ?? derivedClinicRoleIds;
  const advancedEdited =
    form.agencyRoleIdsOverride !== null || form.clinicRoleIdsOverride !== null;

  const toggleAgencyRole = useCallback(
    (roleId: string) => {
      setForm(previous => {
        const current = previous.agencyRoleIdsOverride ?? derivedAgencyRoleIds;
        return {
          ...previous,
          agencyRoleIdsOverride: current.includes(roleId)
            ? current.filter(id => id !== roleId)
            : [...current, roleId],
        };
      });
    },
    [derivedAgencyRoleIds]
  );

  const editClinicRoles = useCallback(
    (
      change: (current: Record<string, string[]>) => Record<string, string[]>
    ) => {
      setForm(previous => ({
        ...previous,
        clinicRoleIdsOverride: change(
          previous.clinicRoleIdsOverride ?? derivedClinicRoleIds
        ),
      }));
    },
    [derivedClinicRoleIds]
  );

  const toggleClinicForRole = useCallback(
    (roleId: string, clinicId: string) => {
      editClinicRoles(current => {
        const held = current[roleId] ?? [];
        return {
          ...current,
          [roleId]: held.includes(clinicId)
            ? held.filter(id => id !== clinicId)
            : [...held, clinicId],
        };
      });
    },
    [editClinicRoles]
  );

  const selectAllClinicsForRole = useCallback(
    (roleId: string, allClinicIds: string[]) => {
      editClinicRoles(current => ({ ...current, [roleId]: allClinicIds }));
    },
    [editClinicRoles]
  );

  const clearClinicsForRole = useCallback(
    (roleId: string) => {
      editClinicRoles(current => ({ ...current, [roleId]: [] }));
    },
    [editClinicRoles]
  );

  /**
   * The matrix in the shape the endpoint takes, or null while untouched.
   *
   * Null leaves the server to derive it, which keeps the untouched path exactly
   * as it was: the client only becomes authoritative once somebody has actually
   * looked at the grid and changed it.
   */
  const clinicRolesSubmission = useMemo(() => {
    if (!advancedEdited) return null;
    return Object.entries(clinicRoleIds)
      .filter(([, ids]) => ids.length > 0)
      .map(([roleId, ids]) => ({ role_id: roleId, clinic_ids: ids }));
  }, [advancedEdited, clinicRoleIds]);

  // Null hands the decision back to the server, which derives from the agency's
  // own archetype configuration and knows nothing about the narrowed list. That
  // is right while the whole catalogue is on screen and wrong once it is not:
  // an unedited submit would grant the EMR roles that configuration happens to
  // name, none of which the operator was shown. So without the EMR surface the
  // filtered list is sent as the decision it now is.
  const agencyRolesSubmission = useMemo(
    () => (advancedEdited || !showEmrUserManagement ? agencyRoleIds : null),
    [advancedEdited, agencyRoleIds, showEmrUserManagement]
  );

  /** How many grants the matrix holds, so the shut section can say so. */
  const advancedGrantCount = useMemo(
    () =>
      agencyRoleIds.length +
      Object.values(clinicRoleIds).filter(ids => ids.length > 0).length,
    [agencyRoleIds, clinicRoleIds]
  );

  const toggleClinic = useCallback(
    (clinicId: string) => {
      setForm(previous => {
        // The first toggle takes the derived all-checked list into state, so
        // unchecking one clinic keeps the rest rather than starting from empty.
        const current = previous.clinicsTouched
          ? previous.clinicIds
          : clinics.map(clinic => clinic.id);
        return {
          ...previous,
          clinicsTouched: true,
          clinicIds: current.includes(clinicId)
            ? current.filter(id => id !== clinicId)
            : [...current, clinicId],
        };
      });
    },
    [clinics]
  );

  const toggleTitle = useCallback((title: UserType) => {
    setForm(previous => ({
      ...previous,
      userTypes: previous.userTypes.includes(title)
        ? previous.userTypes.filter(t => t !== title)
        : [...previous.userTypes, title],
      // Changing titles means the derived set changes, so an override that was
      // never touched should not pin the old value.
      disciplinesOverride: previous.disciplinesOverride,
    }));
  }, []);

  /**
   * Why the form cannot be submitted yet, addressed to the field it is about so
   * the dialog can show it there. A message under the submit button leaves the
   * operator hunting for which input it means.
   *
   * Mirrors the backend's refusals so the dialog does not have to send a request
   * to find out; the backend still checks, since it is the authority.
   */
  const blocker = useMemo((): OnboardBlocker | null => {
    if (!form.email.trim()) {
      return { field: 'email', message: 'Enter their work email address.' };
    }
    // The submit button is not a native form submit, so nothing else checks
    // this. Left to the server, the member is written and committed before
    // WorkOS rejects the address, leaving somebody who exists and can never
    // log in.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return {
        field: 'email',
        message: 'That does not look like an email address.',
      };
    }
    if (option.needsClinics && effectiveClinicIds.length === 0) {
      return {
        field: 'clinics',
        message:
          'Choose at least one clinic, otherwise they cannot be scheduled.',
      };
    }
    if (option.needsTitles && form.userTypes.length === 0) {
      return {
        field: 'titles',
        message:
          'Choose at least one clinical title, which decides the visits they can take.',
      };
    }
    if (option.needsClinics && disciplines.length === 0) {
      return {
        field: 'titles',
        message:
          'These titles allow no visit types, so they could not be scheduled.',
      };
    }
    // Back office is too varied to have a useful default: what one agency's
    // biller needs bears no relation to another's intake coordinator, and the
    // archetype grants nothing on its own. So the roles are the question, and
    // Advanced is where it gets asked.
    if (option.picksAgencyRoles && agencyRoleIds.length === 0) {
      return {
        field: 'agencyRoles',
        message:
          'Choose at least one role under Advanced — this kind of member gets none by default.',
      };
    }
    if (form.appAccountMode === 'link' && !form.appAccountId) {
      return {
        field: 'appAccount',
        message: 'Choose the Roger App account to link.',
      };
    }
    if (form.appAccountMode === 'create') {
      const problem = rogerAppUsernameProblem(username);
      if (problem) return { field: 'appAccount', message: problem };
    }
    return null;
  }, [
    form.email,
    effectiveClinicIds,
    form.userTypes,
    form.appAccountMode,
    form.appAccountId,
    option,
    disciplines,
    username,
    agencyRoleIds,
  ]);

  const clinicNames = useMemo(
    () =>
      effectiveClinicIds
        .map(id => clinics.find(clinic => clinic.id === id)?.name)
        .filter((name): name is string => !!name),
    [effectiveClinicIds, clinics]
  );

  /**
   * What is about to happen, as a sentence. This is the confirmation: a matrix
   * of checkboxes can be misread, a sentence naming the person and what they
   * will be able to do cannot.
   */
  const confirmSentence = useMemo(() => {
    const name = [form.firstName, form.lastName].filter(Boolean).join(' ');
    const who = name || form.email || 'This person';

    const parts: string[] = ['will be able to log into the portal'];
    if (form.appAccountMode === 'create' && username) {
      parts.push(`and into the Roger App as ${username}`);
    } else if (form.appAccountMode === 'link') {
      parts.push('and into the Roger App with their existing account');
    }

    if (form.archetype === 'BACK_OFFICE') {
      // The app account is read off the decision rather than hard-coded, because
      // back office can now come with one: an agency with no EMR lands here for
      // everybody it adds, including the people who work in the Roger App.
      const noAccount = form.appAccountMode === 'none';
      if (showEmrUserManagement) {
        return noAccount
          ? `${who} ${parts.join(' ')}. They will not appear in scheduling and get no Roger App account.`
          : `${who} ${parts.join(' ')}. They will not appear in scheduling.`;
      }
      // Scheduling is only worth mentioning where there is scheduling to stay
      // out of. Naming it to an agency with no EMR describes a screen they do
      // not have.
      return noAccount
        ? `${who} ${parts.join(' ')} and get no Roger App account.`
        : `${who} ${parts.join(' ')}.`;
    }

    const visits = disciplines.map(disciplineProse).join(' and ');
    const where = clinicNames.join(' and ');
    let scheduling = '';
    if (visits && where) {
      scheduling =
        form.archetype === 'CLINICAL_SUPERVISOR'
          ? `, be scheduled for ${visits} visits and act as case manager at ${where}`
          : `, and be scheduled for ${visits} visits at ${where}`;
    }

    const employment = form.employeeType
      ? `, as a ${form.employeeType.replace(/_/g, ' ').toLowerCase()} employee`
      : '';

    // Only an edit is worth mentioning: the matrix always holds whatever the
    // archetype grants, so a count of it would say nothing. Naming the roles
    // would mean carrying role names in here, and the point is just that the
    // sentence stops claiming to be the whole story.
    const extras = advancedEdited
      ? ' Their roles were adjusted under Advanced.'
      : '';

    return `${who} ${parts.join(' ')}${scheduling}${employment}.${extras}`;
  }, [
    form.firstName,
    form.lastName,
    form.email,
    form.archetype,
    form.appAccountMode,
    form.employeeType,
    username,
    disciplines,
    clinicNames,
    advancedEdited,
    showEmrUserManagement,
  ]);

  return {
    form,
    patch,
    reset,
    option,
    derivedDisciplines,
    disciplines,
    uselessTitles,
    derivedUsername,
    username,
    appAccountOffered,
    appAccountDefault,
    setArchetype,
    setEmployeeType,
    toggleClinic,
    effectiveClinicIds,
    toggleTitle,
    toggleAgencyRole,
    toggleClinicForRole,
    selectAllClinicsForRole,
    clearClinicsForRole,
    agencyRoleIds,
    clinicRoleIds,
    agencyRolesSubmission,
    clinicRolesSubmission,
    advancedCount: advancedGrantCount,
    blocker,
    existingAccountMatch,
    linkableAccounts,
    linkExistingAccount,
    clinicNames,
    confirmSentence,
  };
}
